import {
  Alert,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Tooltip,
  Text,
  AlertDescription,
  AlertIcon,
  AlertTitle,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColorScheme, CreatableSelect, MultiValue } from "chakra-react-select";
import { AlertsContext } from "providers/alerts";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useContext, useState } from "react";
import { LuInfo } from "react-icons/lu";
import { apiRequest } from "utils/api";
import colors from "utils/colors";
import { selectChakraStyles } from "utils/constants";
import { getErrorMessage } from "utils/errorHandler";
import { checkUiConfiguration, getCustomButtonVariant } from "utils/uiconfighelper";
import { FieldViewModel } from "./field.view-model";
import { LoanTypeViewModel } from "../loan-types/loan-type.view-model";
import { fieldTypes } from "./field-types";
import { DeveloperBox } from "pages/Settings/developer-mode/developer-box";

interface FieldModalProps {
  field: FieldViewModel;
  loanType?: LoanTypeViewModel | null;
  section?: any | null;
  isOpen: boolean;
  onClose: () => void;
  onAdd: (field: FieldViewModel) => void;
  onUpdate?: (field: FieldViewModel) => void;
  onDelete?: (field: FieldViewModel) => void;
}

export function FieldModal({
  field,
  loanType,
  section,
  isOpen,
  onClose,
  onAdd,
  onUpdate,
  onDelete,
}: FieldModalProps) {
  const { user } = useTenant();

  const applicationTerm =
    user?.company?.id === "2e6eae2b-de55-4c49-96c6-ff77740565f1" ||
    user?.company?.id === "59d63802-e33d-4b99-9ddd-e92004455eb3"
      ? "Term Sheet"
      : "Application";

  const colorScheme: ColorScheme = "teal";

  const { showSuccess, showError } = useContext(AlertsContext);

  const [label, setLabel] = useState(field.label);
  const [type, setType] = useState(field.type);
  const [options, setOptions] = useState<string[]>(field.options ?? []);
  const [datakey] = useState(field.data_key || "");
  const [placeholder, setPlaceholder] = useState(field.placeholder);
  const [editable, setEditable] = useState(field.editable);
  const [required, setRequired] = useState(field.required);
  const [underwriting, setUnderwriting] = useState(field.underwriting);
  const [calculated, setCalculated] = useState(field.calculated);
  const [dataKey, setDataKey] = useState(field.data_key || "");
  const [identifierKey, setIdentifierKey] = useState(field.identifier_key || "");

  const [show_on_lender_application, setShowOnLenderApplication] = useState(
    field.show_on_lender_application
  );

  const [show_on_lender_loan, setShowOnLenderLoan] = useState(field.show_on_lender_loan);

  const [show_on_borrower_application, setShowOnBorrowerApplication] = useState(
    field.show_on_borrower_application
  );

  const [show_on_borrower_loan, setShowOnBorrowerLoan] = useState(field.show_on_borrower_loan);

  const [do_not_allow_borrower_to_edit, setDoNotAllowBorrowerToEdit] = useState(
    field.do_not_allow_borrower_to_edit
  );

  const [isLoading, setIsLoading] = useState(false);

  const [startDelete, setStartDelete] = useState(false);
  const [, setShowButtons] = useState(false);

  let modalTitle = field.id ? "Edit Loan Field" : "Add Loan Field";

  if (section) {
    modalTitle = `${section.section_name}: ${modalTitle}`;
  }

  if (loanType) {
    modalTitle = `${loanType.title}: ${modalTitle}`;
  }

  const modalSubmitText = field.id ? "Save Changes" : "Add New Field";

  function isLoanSectionEnabled() {
    return checkUiConfiguration(user, "loan_section");
  }

  async function handle(fn: () => Promise<FieldViewModel>, errorTitle: string) {
    try {
      setIsLoading(true);

      const field = await fn();

      showSuccess({
        title: "Success",
        description: "The request was successful!",
      });

      onClose();

      return field;
    } catch (err) {
      showError({
        title: errorTitle,
        description: getErrorMessage(err),
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  }

  async function addField(): Promise<FieldViewModel> {
    const newField: NewField = {
      label: label,
      type: type,
      placeholder: placeholder,
      required: required,
      underwriting: underwriting,
      options: options,
      sectionId: section?.id,
      calculated: calculated,
      editable: editable,
      order: null,
      data_key: dataKey,
      identifier_key: identifierKey,
      show_on_lender_application: show_on_lender_application,
      show_on_lender_loan: show_on_lender_loan,
      show_on_borrower_application: show_on_borrower_application,
      show_on_borrower_loan: show_on_borrower_loan,
      do_not_allow_borrower_to_edit: do_not_allow_borrower_to_edit,
    };

    const addedField = await apiRequest<AddedField>("/supabase/loan_fields/insert", {
      loan_type_id: loanType?.id,
      field: newField,
    });

    return {
      calculated: addedField.calculated ?? false,
      data_key: addedField.data_key ?? "",
      editable: addedField.editable ?? false,
      id: addedField.id,
      label: addedField.label,
      options: addedField.options ?? [],
      order: addedField.order ?? 0,
      placeholder: addedField.placeholder ?? "",
      required: addedField.required,
      sectionId: addedField.section ?? null,
      type: addedField.type,
      identifier_key: addedField.identifier_key ?? "",
      underwriting: addedField.underwriting,
      show_on_lender_application: addedField.show_on_lender_application,
      show_on_lender_loan: addedField.show_on_lender_loan,
      show_on_borrower_application: addedField.show_on_borrower_application,
      show_on_borrower_loan: addedField.show_on_borrower_loan,
      do_not_allow_borrower_to_edit: addedField.do_not_allow_borrower_to_edit,
      loan_type: addedField.loan_type,
    };
  }

  async function updateField(): Promise<FieldViewModel> {
    const updateField: UpdateField = {
      label: label,
      type: type,
      placeholder: placeholder,
      required: required,
      underwriting: underwriting,
      options: options,
      editable: editable,
      calculated: calculated,
      data_key: dataKey,
      identifier_key: identifierKey,
      show_on_lender_application: show_on_lender_application,
      show_on_lender_loan: show_on_lender_loan,
      show_on_borrower_application: show_on_borrower_application,
      show_on_borrower_loan: show_on_borrower_loan,
      do_not_allow_borrower_to_edit: do_not_allow_borrower_to_edit,
    };

    await apiRequest("/supabase/loan_fields/update", {
      loan_field_id: field.id,
      field: updateField,
    });

    return {
      ...field,
      ...updateField,
    };
  }

  async function deleteField(): Promise<FieldViewModel> {
    await apiRequest("/supabase/loan_fields/delete", {
      loan_field_id: field.id,
    });

    return field;
  }

  async function handleSaveField() {
    if (!label || !type) {
      showError({
        title: "Error",
        description: "Please, fill in all required fields.",
      });
      return;
    }

    if (field.id) {
      const result = await handle(updateField, "Update Failed");
      onUpdate?.(result);
    } else {
      const result = await handle(addField, "Add Failed");
      onAdd?.(result);
    }
  }

  async function handleDeleteField() {
    await handle(deleteField, "Delete Failed");
    onDelete?.(field);
  }

  const renderDeveloperMode = () => {
    return (
      <DeveloperBox mb={5} mt={4}>
        {/* CALCULATED */}
        <FormControl mb="12px">
          <Checkbox
            colorScheme={colorScheme}
            isChecked={calculated}
            onChange={ev => setCalculated(ev.target.checked)}
          >
            Calculated
          </Checkbox>
        </FormControl>
        {/* / CALCULATED */}

        {/* DATA KEY */}
        <FormControl mb="12px">
          <FormLabel>Data Key</FormLabel>
          <Input
            value={dataKey}
            onChange={ev => setDataKey(ev.target.value)}
            placeholder="Data Key"
            variant="primary"
          />
        </FormControl>
        {/* / DATA KEY */}

        {/* IDENTIFIER KEY */}
        <FormControl mb="12px">
          <FormLabel>Identifier Key</FormLabel>
          <Input
            value={identifierKey}
            onChange={ev => setIdentifierKey(ev.target.value)}
            placeholder="Identifier Key"
            variant="primary"
          />
        </FormControl>
        {/* / IDENTIFIER KEY */}
      </DeveloperBox>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{modalTitle}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* LABEL */}
          <FormControl mb="12px" isRequired>
            <FormLabel display="flex" alignItems="center">
              Field Name
              <Tooltip hasArrow label="Field name." aria-label="Field Name" placement="right">
                <Icon fontSize="lg" style={{ marginLeft: "5px" }}>
                  <LuInfo />
                </Icon>
              </Tooltip>
            </FormLabel>
            <Input
              value={label}
              onChange={ev => setLabel(ev.target.value)}
              placeholder="Field Name"
              variant="primary"
            />
          </FormControl>
          {/* / LABEL */}

          {/* FIELD TYPE */}
          <FormControl mb="12px" isRequired>
            <FormLabel>
              Field Type
              <Tooltip
                hasArrow
                label="Data format for this field."
                aria-label="Data Format For Field"
                placement="right"
              >
                <Icon fontSize="lg" style={{ marginLeft: "5px" }}>
                  <LuInfo />
                </Icon>
              </Tooltip>
            </FormLabel>
            <Select value={type} onChange={ev => setType(ev.target.value)}>
              <option value="" disabled>
                Select Field Type...
              </option>
              {Object.entries(fieldTypes)
                .sort((a, b) => a[1].localeCompare(b[1]))
                .map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
            </Select>
          </FormControl>

          {field.id && field.type && type !== field.type && (
            <Alert
              mb="12px"
              borderRadius="8px"
              fontSize="14px"
              status="info"
              border="1px"
              borderColor="#0D69D4"
            >
              <Flex alignItems="flex-start">
                <span>
                  {" "}
                  <FontAwesomeIcon
                    icon={["far", "circle-exclamation"]}
                    style={{ color: "#0D69D4" }}
                  ></FontAwesomeIcon>
                </span>
                <Box style={{ marginLeft: "5px" }}>
                  Changing the field type could result in existing data being formatted incorrectly.
                  Consider adding a new field instead.
                </Box>
              </Flex>
            </Alert>
          )}
          {/* / TYPE */}

          {/* OPTIONS */}
          {(type === "dropdown" || type === "radio") && (
            <FormControl mb="12px">
              <FormLabel>Options</FormLabel>
              <CreatableSelect
                isMulti
                useBasicStyles
                colorScheme={colorScheme}
                variant="outline"
                chakraStyles={selectChakraStyles}
                value={options.map((option: any) => ({
                  label: option,
                  value: option,
                }))}
                hideSelectedOptions
                focusBorderColor={colors.GRAY_50}
                onChange={
                  ((
                    val: MultiValue<any> //this is {label,value}
                  ) => {
                    setOptions(val.map(v => v.value));
                  }) as any
                }
                options={options.map(option => ({
                  label: option,
                  value: option,
                }))}
                onCreateOption={(val: string) => {
                  setOptions([...options, val]);
                }}
              />
            </FormControl>
          )}
          {/* / OPTIONS */}

          {/* PLACEHOLDER */}
          <FormControl mb="12px">
            <FormLabel>
              Placeholder Text
              <Tooltip
                hasArrow
                label="Placeholder text for this field."
                aria-label="Placeholder text For Field"
                placement="right"
              >
                <Icon fontSize="lg" style={{ marginLeft: "5px" }}>
                  <LuInfo />
                </Icon>
              </Tooltip>
            </FormLabel>
            <Input
              value={placeholder}
              onChange={ev => setPlaceholder(ev.target.value)}
              placeholder="Placeholder Text"
              variant="primary"
            />
          </FormControl>
          {/* / PLACEHOLDER */}

          {/* SETTINGS */}
          <Box mb="20px" mt="1rem">
            <Text fontWeight="medium" fontSize="sm" mb="6px">
              Settings
            </Text>

            {/* REQUIRED */}
            <FormControl mb="12px">
              <Checkbox
                colorScheme={colorScheme}
                isChecked={required}
                onChange={ev => setRequired(ev.target.checked)}
              >
                Required
              </Checkbox>
              <Tooltip hasArrow label="This field must be filled out." placement="right">
                <Icon fontSize="lg" style={{ marginLeft: "5px" }}>
                  <LuInfo />
                </Icon>
              </Tooltip>
            </FormControl>
            {/* / REQUIRED */}

            {/* NOT LOAN SECTION */}
            {!isLoanSectionEnabled() && (
              <>
                {/* EDITABLE */}
                <FormControl mb="12px">
                  <Checkbox
                    colorScheme={colorScheme}
                    isChecked={editable}
                    onChange={ev => setEditable(ev.target.checked)}
                  >
                    Editable
                  </Checkbox>
                  <Tooltip hasArrow label="This field will be editable." placement="right">
                    <Icon fontSize="lg" style={{ marginLeft: "5px" }}>
                      <LuInfo />
                    </Icon>
                  </Tooltip>
                </FormControl>
                {/* / EDITABLE */}

                {/* UNDERWRITING */}
                <FormControl mb="12px">
                  <Checkbox
                    colorScheme={colorScheme}
                    isChecked={underwriting}
                    onChange={ev => setUnderwriting(ev.target.checked)}
                  >
                    Underwriting
                  </Checkbox>
                  <Tooltip
                    hasArrow
                    label="This field will be displayed in the underwriting section."
                    placement="right"
                  >
                    <Icon fontSize="lg" style={{ marginLeft: "5px" }}>
                      <LuInfo />
                    </Icon>
                  </Tooltip>
                </FormControl>
                {/* / UNDERWRITING */}
              </>
            )}
            {/* / NOT LOAN SECTION */}
          </Box>
          {/* / SETTINGS */}

          {/* LENDER VIEW SETTINGS */}
          <Box mb="20px">
            <Text fontWeight="medium" fontSize="sm" mb="6px">
              Lender View Settings
            </Text>

            {/* SHOW FIELD ON LENDER APPLICATION */}
            <FormControl mb="12px">
              <Checkbox
                colorScheme={colorScheme}
                isChecked={show_on_lender_application}
                onChange={ev => setShowOnLenderApplication(ev.target.checked)}
              >
                Show field on Lender {applicationTerm}
              </Checkbox>
              <Tooltip
                hasArrow
                label={`This field will show up on the ${applicationTerm.toLowerCase()}.`}
                placement="right"
              >
                <Icon fontSize="lg" style={{ marginLeft: "5px" }}>
                  <LuInfo />
                </Icon>
              </Tooltip>
            </FormControl>
            {/* / SHOW FIELD ON LENDER APPLICATION */}

            {/* SHOW FIELD ON LENDER LOAN */}
            <FormControl mb="12px">
              <Checkbox
                colorScheme={colorScheme}
                isChecked={show_on_lender_loan}
                onChange={ev => setShowOnLenderLoan(ev.target.checked)}
              >
                Show field on Lender Loan
              </Checkbox>
              <Tooltip hasArrow label="This field will show up on the loan." placement="right">
                <Icon fontSize="lg" style={{ marginLeft: "5px" }}>
                  <LuInfo />
                </Icon>
              </Tooltip>
            </FormControl>
            {/* / SHOW FIELD ON LENDER LOAN */}
          </Box>
          {/* / LENDER VIEW SETTINGS */}

          {/* BORROWER VIEW SETTINGS */}
          <Box>
            <Text fontWeight="medium" fontSize="sm" mb="6px">
              Borrower View Settings
            </Text>

            {/* SHOW FIELD ON BORROWER APPLICATION */}
            <FormControl mb="12px">
              <Checkbox
                colorScheme={colorScheme}
                isChecked={show_on_borrower_application}
                onChange={ev => setShowOnBorrowerApplication(ev.target.checked)}
              >
                Show field on Borrower {applicationTerm}
              </Checkbox>
              <Tooltip
                hasArrow
                label={`This field will show up on the ${applicationTerm.toLowerCase()}.`}
                placement="right"
              >
                <Icon fontSize="lg" style={{ marginLeft: "5px" }}>
                  <LuInfo />
                </Icon>
              </Tooltip>
            </FormControl>
            {/* / SHOW FIELD ON BORROWER APPLICATION */}

            {/* SHOW FIELD ON BORROWER LOAN */}
            <FormControl mb="12px">
              <Checkbox
                colorScheme={colorScheme}
                isChecked={show_on_borrower_loan}
                onChange={ev => setShowOnBorrowerLoan(ev.target.checked)}
              >
                Show field on Borrower Loan
              </Checkbox>
              <Tooltip hasArrow label="This field will show up on the loan." placement="right">
                <Icon fontSize="lg" style={{ marginLeft: "5px" }}>
                  <LuInfo />
                </Icon>
              </Tooltip>
            </FormControl>
            {/* / SHOW FIELD ON BORROWER LOAN */}

            {/* DO NOT ALLOW BORROWER TO EDIT FIELD */}
            <FormControl mb="12px">
              <Checkbox
                colorScheme={colorScheme}
                isChecked={do_not_allow_borrower_to_edit}
                onChange={ev => setDoNotAllowBorrowerToEdit(ev.target.checked)}
              >
                Do not allow borrower to edit field
              </Checkbox>
              <Tooltip
                hasArrow
                label="This field will not be editable by the borrower."
                placement="right"
              >
                <Icon fontSize="lg" style={{ marginLeft: "5px" }}>
                  <LuInfo />
                </Icon>
              </Tooltip>
            </FormControl>
            {/* / DO NOT ALLOW BORROWER TO EDIT FIELD */}
          </Box>
          {/* / BORROWER VIEW SETTINGS */}

          {/* DEVELOPER MODE */}
          {renderDeveloperMode()}
          {/* / DEVELOPER MODE */}
        </ModalBody>

        {!startDelete ? (
          <ModalFooter gap={2}>
            <Box flex={1}>
              {field.id && (
                <IconButton
                  variant="outline"
                  onClick={() => {
                    setStartDelete(true);
                    setShowButtons(true);
                  }}
                  isDisabled={datakey ? true : false}
                  aria-label="delete-field"
                  icon={<FontAwesomeIcon icon={["far", "trash-can"]} />}
                />
              )}
            </Box>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant={getCustomButtonVariant(user)}
              onClick={handleSaveField}
              isLoading={isLoading}
              leftIcon={<FontAwesomeIcon icon={["far", "save"]} />}
            >
              {modalSubmitText}
            </Button>
          </ModalFooter>
        ) : (
          <>
            <ModalFooter>
              <Alert status="error" borderRadius="8px">
                <AlertIcon />
                <Box>
                  <AlertTitle>Delete loan field?</AlertTitle>
                  <AlertDescription>
                    Deleting a loan field will delete all loan data for this field.
                  </AlertDescription>
                </Box>
              </Alert>
            </ModalFooter>

            <ModalFooter gap={2}>
              <Button
                variant="outline"
                onClick={() => {
                  setShowButtons(false);
                  setStartDelete(false);
                }}
              >
                Not Now
              </Button>
              <Button
                variant="red"
                onClick={handleDeleteField}
                isLoading={isLoading}
                leftIcon={<FontAwesomeIcon icon={["far", "trash-alt"]} />}
              >
                Confirm
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}

interface NewField {
  label: string;
  type: string;
  sectionId: string | null;
  options: string[];
  placeholder: string;
  order: number | null;
  editable: boolean;
  required: boolean;
  underwriting: boolean;
  calculated: boolean;
  data_key: string | null;
  identifier_key: string | null;
  show_on_lender_application: boolean;
  show_on_lender_loan: boolean;
  show_on_borrower_application: boolean;
  show_on_borrower_loan: boolean;
  do_not_allow_borrower_to_edit: boolean;
}

interface UpdateField {
  label: string;
  type: string;
  options: string[];
  placeholder: string;
  required: boolean;
  underwriting: boolean;
  editable: boolean;
  calculated: boolean;
  data_key: string | null;
  identifier_key: string | null;
  show_on_lender_application: boolean;
  show_on_lender_loan: boolean;
  show_on_borrower_application: boolean;
  show_on_borrower_loan: boolean;
  do_not_allow_borrower_to_edit: boolean;
}

interface AddedField {
  calculated: boolean | null;
  company: string;
  created_at: string | null;
  data_key: string | null;
  identifier_key: string | null;
  editable: boolean | null;
  id: string;
  label: string;
  loan_type: string | null;
  options: string[] | null;
  order: number | null;
  placeholder: string | null;
  required: boolean;
  section: string | null;
  type: string;
  underwriting: boolean;
  show_on_lender_application: boolean;
  show_on_lender_loan: boolean;
  show_on_borrower_application: boolean;
  show_on_borrower_loan: boolean;
  do_not_allow_borrower_to_edit: boolean;
}
